import { ApiResponse, Question, Session, TicketData, DocumentsResponse, FeedbackData, FeedbackResponse } from "../types/chat.types";
import axios, { AxiosError } from "axios";

const API_BASE_URL = "/api2";

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public isNetworkError?: boolean,
    public isSessionError?: boolean
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiService {
  private api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      "Content-Type": "application/json",
    },
    timeout: 60000,
  });

  constructor() {
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.code === 'ECONNABORTED') {
          throw new ApiError('Anfrage-Timeout (60s) - Server ist möglicherweise langsam', 408, false);
        }
        
        if (!error.response) {
          throw new ApiError('Netzwerkfehler - bitte überprüfen Sie Ihre Internetverbindung', 0, true);
        }

        const status = error.response.status;
        const isSessionError = status === 404 || (status === 500 && error.config?.url?.includes('/chat/'));
        
        switch (status) {
          case 400:
            throw new ApiError('Ungültiges Anfrageformat', status);
          case 404:
            throw new ApiError('Sitzung nicht gefunden oder abgelaufen', status, false, true);
          case 500:
            if (isSessionError) {
              throw new ApiError('Sitzungsfehler - starte neue Unterhaltung', status, false, true);
            }
            throw new ApiError('Serverfehler - bitte versuchen Sie es später erneut', status);
          case 503:
            throw new ApiError('Service vorübergehend nicht verfügbar', status);
          default:
            throw new ApiError(`Anfrage fehlgeschlagen mit Status ${status}`, status);
        }
      }
    );
  }

  async ask(question: string): Promise<ApiResponse> {
    const response = await this.api.post<ApiResponse>("/api/ask", {
      question: question,
    });
    return response.data;
  }

  async startSession(question: Question): Promise<Session> {
    const response = await this.api.post<Session>("/chat/start", {
      user_input: question.question,
    });
    return response.data;
  }

  async askInSession(
    sessionId: string,
    question: Question
  ): Promise<ApiResponse> {
    const response = await this.api.post<ApiResponse>(
      `/chat/${sessionId}/ask`,
      { user_input: question.question }
    );
    return response.data;
  }

  async deleteSession(sessionId: string): Promise<void> {
    await this.api.delete(`/chat/${sessionId}`);
  }

  async getDocumentByName(documentName: string): Promise<Blob | null> {
    try {
      // Ensure .pdf extension
      const filename = documentName.includes('.') ? documentName : `${documentName}.pdf`;
      const response = await this.api.get(`/api/documents/${filename}/download`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error("Fehler beim Laden des Dokuments:", error);
      return null;
    }
  }

  async submitTicket(ticketData: TicketData): Promise<any> {
    try {
      const response = await this.api.post('/api/tickets', ticketData);
      return response.data;
    } catch (error) {
      console.error("Fehler beim Senden des Tickets:", error);
      throw error; // Re-throw to let FeedbackDialog handle it
    }
  }

  async getAllDocuments(): Promise<DocumentsResponse> {
    try {
      const response = await this.api.get<DocumentsResponse>('/api/documents/all');
      return response.data;
    } catch (error) {
      console.error("Fehler beim Laden aller Dokumente:", error);
      throw error;
    }
  }

  async submitFeedback(feedbackData: FeedbackData): Promise<FeedbackResponse> {
    try {
      const response = await this.api.post<FeedbackResponse>('/api/feedback', feedbackData);
      return response.data;
    } catch (error) {
      console.error("Fehler beim Senden des Feedbacks:", error);
      throw error;
    }
  }
}

export const apiService = new ApiService();